/**
 * EmailContextTab - Email Correspondence View for email_task work items
 * 
 * Shows the full email thread/conversation that is linked to this task.
 * All replies (inbound and outbound) are displayed in chronological order.
 * 
 * Date: January 2026
 * Updated: January 28, 2026 - Enhanced to show full thread correspondence
 */

'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail,
  User,
  Users,
  Calendar,
  Paperclip,
  Download,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Reply,
  ReplyAll,
  Forward,
  Inbox,
  ArrowDownLeft,
  ArrowUpRight,
  MessageSquare,
  Clock,
  Copy
} from 'lucide-react';
import { notify } from '@/lib/notifications';
import { authService } from '@/lib/authService';
import { format, formatDistanceToNow } from 'date-fns';

interface EmailContextTabProps {
  workItemId: string;
  isActive: boolean;
  isDark?: boolean;
  mailboxEmail?: string; // The shared mailbox email address to detect direction
  onReply?: (emailData: { email: any; all?: boolean }) => void;
}

interface EmailAddress {
  address: string;
  name?: string;
}

interface EmailAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  url?: string | null;
  directUrl?: string | null;
  storage_token?: string | null;
}

interface ThreadMessage {
  messageId: string;
  from: EmailAddress;
  to: EmailAddress[];
  cc?: EmailAddress[];
  bcc?: EmailAddress[];
  subject: string;
  date: string;
  html: string;
  text: string;
  attachments: EmailAttachment[];
  folder?: string;
  flags?: string[];
  isRead?: boolean;
  inReplyTo?: string;
  references?: string[];
}

interface EmailMetadata {
  email_from: EmailAddress;
  email_to: EmailAddress[];
  email_cc?: EmailAddress[];
  email_subject: string;
  email_date: string;
  has_attachments: boolean;
  attachment_count: number;
  account_id?: number;
  mongodb_message_id?: string;
}

interface EmailResponse {
  success: boolean;
  emailCached: boolean;
  email?: ThreadMessage;
  threadMessages?: ThreadMessage[];
  metadata?: EmailMetadata;
  threadId?: string;
  threadMessageCount?: number;
  message?: string;
  error?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatEmailAddress = (addr: EmailAddress) => {
  if (addr.name) {
    return `${addr.name} <${addr.address}>`;
  }
  return addr.address;
};

const getInitials = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (email: string): string => {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
    'bg-orange-500', 'bg-pink-500', 'bg-teal-500',
    'bg-indigo-500', 'bg-red-500', 'bg-cyan-500'
  ];
  const index = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};

// Sanitize email HTML
const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  let sanitized = html;
  // Remove meta tags
  sanitized = sanitized.replace(/<meta[^>]*\/?>/gi, '');
  // Remove DOCTYPE
  sanitized = sanitized.replace(/<!DOCTYPE[^>]*>/gi, '');
  // Remove html/head/body tags
  sanitized = sanitized.replace(/<\/?html[^>]*>/gi, '');
  sanitized = sanitized.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
  sanitized = sanitized.replace(/<\/?body[^>]*>/gi, '');
  // Upgrade HTTP to HTTPS for images
  sanitized = sanitized.replace(/(<img[^>]+src=["'])http:\/\//gi, '$1https://');
  return sanitized;
};

// ============================================================================
// EMAIL MESSAGE COMPONENT
// ============================================================================

interface EmailMessageProps {
  message: ThreadMessage;
  isDark: boolean;
  isOutbound: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  isFirst: boolean;
  isLast: boolean;
}

function EmailMessage({ 
  message, 
  isDark, 
  isOutbound, 
  isExpanded, 
  onToggle,
  isFirst,
  isLast
}: EmailMessageProps) {
  const from = message.from;
  const displayName = from?.name || from?.address || 'Unknown';
  const hasAttachments = message.attachments && message.attachments.length > 0;
  
  return (
    <div className="relative">
      {/* Thread line connector */}
      {!isLast && (
        <div 
          className={`absolute left-5 top-12 bottom-0 w-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}
        />
      )}
      
      <div className={`relative flex gap-3 ${!isLast ? 'pb-4' : ''}`}>
        {/* Avatar */}
        <div className="flex-shrink-0 z-10">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${getAvatarColor(from?.address || '')}`}>
            {getInitials(displayName)}
          </div>
        </div>
        
        {/* Message Card */}
        <div className={`flex-1 rounded-lg border overflow-hidden ${
          isOutbound 
            ? isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
            : isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header - Always visible */}
          <button
            onClick={onToggle}
            className={`w-full px-4 py-3 flex items-start justify-between text-left transition-colors ${
              isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50/80'
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Direction indicator */}
                <span className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                  isOutbound 
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                }`}>
                  {isOutbound ? (
                    <>
                      <ArrowUpRight className="w-3 h-3" />
                      Sent
                    </>
                  ) : (
                    <>
                      <ArrowDownLeft className="w-3 h-3" />
                      Received
                    </>
                  )}
                </span>
                
                {/* Sender name */}
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {displayName}
                </span>
                
                {/* Email address if different from name */}
                {from?.name && (
                  <span className="text-gray-500 dark:text-gray-400 text-sm truncate">
                    &lt;{from?.address}&gt;
                  </span>
                )}
              </div>
              
              {/* Subject line preview (collapsed) */}
              {!isExpanded && (
                <p className={`text-sm mt-1 truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {message.subject}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
              {/* Attachment indicator */}
              {hasAttachments && (
                <Paperclip className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
              
              {/* Timestamp */}
              <span className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {format(new Date(message.date), "MMM d, HH:mm")}
              </span>
              
              {/* Expand/collapse icon */}
              {isExpanded ? (
                <ChevronUp className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              ) : (
                <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </div>
          </button>
          
          {/* Expanded content */}
          {isExpanded && (
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              {/* Recipients */}
              <div className={`px-4 py-2 text-sm space-y-1 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50/50'}`}>
                <div className="flex gap-2">
                  <span className="text-gray-500 dark:text-gray-400 w-8">To:</span>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    {message.to?.map(formatEmailAddress).join(', ')}
                  </span>
                </div>
                {message.cc && message.cc.length > 0 && (
                  <div className="flex gap-2">
                    <span className="text-gray-500 dark:text-gray-400 w-8">Cc:</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {message.cc.map(formatEmailAddress).join(', ')}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Subject (expanded) */}
              <div className={`px-4 py-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {message.subject || '(No subject)'}
                </h4>
              </div>
              
              {/* Attachments */}
              {hasAttachments && (
                <div className={`px-4 py-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {message.attachments.length} attachment{message.attachments.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {message.attachments.map((att, i) => {
                      // Build download URL: prefer directUrl, then url, then cloud-storage with token
                      const downloadUrl = att.directUrl || att.url || 
                        (att.storage_token ? `https://cloud-storage.totalaccess.co.za/api/v1/attachments/${att.storage_token}/download` : null);
                      
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                            isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                          }`}
                        >
                          <span className={`text-sm truncate max-w-[200px] ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                            {att.filename}
                          </span>
                          <span className="text-xs text-gray-400">
                            ({formatFileSize(att.size)})
                          </span>
                          {downloadUrl && (
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                              title="Download attachment"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Email body */}
              <div className={`px-4 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                {message.html ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.html) }}
                    className={`email-content prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''} overflow-x-auto`}
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  />
                ) : message.text ? (
                  <pre className={`whitespace-pre-wrap font-sans text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {message.text}
                  </pre>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic">
                    No email content available
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function EmailContextTab({
  workItemId,
  isActive,
  isDark = false,
  mailboxEmail,
  onReply,
}: EmailContextTabProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailCached, setEmailCached] = useState(false);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [metadata, setMetadata] = useState<EmailMetadata | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [threadId, setThreadId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const previousWorkItemIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadEmailData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setError('Not authenticated');
        return;
      }

      console.log('[EmailContextTab] Fetching email data...');
      const response = await fetch(`/api/v1/email/work-items/${encodeURIComponent(workItemId)}/email`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
        },
      });

      const result: EmailResponse = await response.json();
      console.log('[EmailContextTab] API response:', {
        success: result.success,
        emailCached: result.emailCached,
        threadMessageCount: result.threadMessages?.length,
        hasMetadata: !!result.metadata,
        error: result.error
      });

      if (!result.success) {
        setError(result.error || 'Failed to load email data');
        return;
      }

      setEmailCached(result.emailCached);
      setMetadata(result.metadata || null);
      setThreadId(result.threadId || null);
      
      // Use threadMessages if available, otherwise wrap single email
      if (result.threadMessages && result.threadMessages.length > 0) {
        console.log('[EmailContextTab] Setting threadMessages:', result.threadMessages.length);
        setThreadMessages(result.threadMessages);
      } else if (result.email) {
        setThreadMessages([result.email]);
      } else {
        // No email data available (thread not found in MongoDB)
        setThreadMessages([]);
      }
    } catch (err: any) {
      console.error('Error loading email data:', err);
      setError(err.message || 'Failed to load email');
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    // Reset if workItemId changes
    if (previousWorkItemIdRef.current !== workItemId) {
      hasLoadedRef.current = false;
      previousWorkItemIdRef.current = workItemId;
      setThreadMessages([]);
      setMetadata(null);
      setError(null);
      setEmailCached(false);
    }
    
    if (isActive && !hasLoadedRef.current) {
      loadEmailData();
      hasLoadedRef.current = true;
    }
  }, [isActive, workItemId, loadEmailData]);

  // Auto-expand the most recent message
  useEffect(() => {
    if (threadMessages.length > 0) {
      const lastMessage = threadMessages[threadMessages.length - 1];
      setExpandedMessages(new Set([lastMessage.messageId]));
    }
  }, [threadMessages]);

  const toggleMessage = (messageId: string) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedMessages(new Set(threadMessages.map(m => m.messageId)));
  };

  const collapseAll = () => {
    setExpandedMessages(new Set());
  };

  // Determine if a message is outbound (sent from our mailbox)
  const isOutboundMessage = (message: ThreadMessage): boolean => {
    // If mailboxEmail is provided, check if FROM matches
    if (mailboxEmail) {
      return message.from?.address?.toLowerCase() === mailboxEmail.toLowerCase();
    }
    
    // Otherwise, check if the email is in the TO field of metadata (original was inbound)
    // and compare with subsequent messages
    if (metadata?.email_to && message.from) {
      const ourAddresses = metadata.email_to.map(t => t.address.toLowerCase());
      return ourAddresses.includes(message.from.address.toLowerCase());
    }
    
    // Check folder - SENT folder means outbound
    if (message.folder?.toLowerCase() === 'sent') {
      return true;
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="text-red-500 dark:text-red-400 text-center">{error}</p>
        <button
          onClick={() => {
            hasLoadedRef.current = false;
            loadEmailData();
          }}
          className="mt-4 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!metadata && threadMessages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Inbox className="w-12 h-12 text-gray-400 mb-3" />
        <p className="text-gray-500 dark:text-gray-400 text-center">
          No email data available for this task
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`p-4 rounded-lg border ${isDark ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-800' : 'bg-indigo-100'}`}>
              <MessageSquare className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className={`font-medium ${isDark ? 'text-indigo-200' : 'text-indigo-900'}`}>
                  Email Correspondence
                </h3>
                <span className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-indigo-800 text-indigo-200' : 'bg-indigo-100 text-indigo-700'}`}>
                  {threadMessages.length} message{threadMessages.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <p className={`text-sm ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  Full email thread for this task
                </p>
                {threadId && (
                  <span className={`text-[10px] sm:text-xs font-mono flex items-center gap-1 ${isDark ? 'text-indigo-400/60' : 'text-indigo-600/60'}`}>
                    Thread: {threadId}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await navigator.clipboard.writeText(threadId);
                          notify.success('Thread ID copied to clipboard');
                        } catch (err) {
                          console.error('Failed to copy:', err);
                        }
                      }}
                      className={`p-0.5 rounded transition-colors ${isDark ? 'hover:bg-indigo-800/50' : 'hover:bg-indigo-200/50'}`}
                      title="Copy Thread ID"
                    >
                      <Copy className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Expand/Collapse controls */}
          {threadMessages.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className={`text-xs px-2 py-1 rounded ${
                  isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                Expand all
              </button>
              <button
                onClick={collapseAll}
                className={`text-xs px-2 py-1 rounded ${
                  isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                Collapse all
              </button>
              <button
                onClick={() => {
                  hasLoadedRef.current = false;
                  loadEmailData();
                }}
                className={`p-1.5 rounded ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Thread Messages */}
      {threadMessages.length > 0 ? (
        <div className="space-y-0">
          {threadMessages.map((message, index) => (
            <EmailMessage
              key={message.messageId}
              message={message}
              isDark={isDark}
              isOutbound={isOutboundMessage(message)}
              isExpanded={expandedMessages.has(message.messageId)}
              onToggle={() => toggleMessage(message.messageId)}
              isFirst={index === 0}
              isLast={index === threadMessages.length - 1}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      ) : metadata ? (
        /* Metadata only - email content not available */
        <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="p-4 space-y-3">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <p className={`text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                <strong>Note:</strong> Email content is not available. Only metadata is shown.
              </p>
            </div>
            
            {/* From */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 dark:text-blue-400 font-medium">
                  {(metadata.email_from?.name || metadata.email_from?.address || '?')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {metadata.email_from?.name || metadata.email_from?.address}
                  </span>
                  {metadata.email_from?.name && (
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      &lt;{metadata.email_from?.address}&gt;
                    </span>
                  )}
                </div>
                {metadata.email_date && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {format(new Date(metadata.email_date), "dd MMM yyyy 'at' HH:mm")}
                  </p>
                )}
              </div>
            </div>

            {/* To */}
            <div className="flex items-start gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400 w-8">To:</span>
              <div className={`flex-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                {metadata.email_to?.map((addr, i) => (
                  <span key={i}>
                    {formatEmailAddress(addr)}
                    {i < metadata.email_to.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div className={`pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {metadata.email_subject || '(No subject)'}
              </h4>
            </div>
          </div>
        </div>
      ) : null}
      
      {/* Reply Actions */}
      {threadMessages.length > 0 && (
        <div className={`flex items-center justify-center gap-3 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {onReply ? (
            <>
              <button
                onClick={() => {
                  const lastMessage = threadMessages[threadMessages.length - 1];
                  const emailForReply = {
                    id: lastMessage.messageId,
                    messageId: lastMessage.messageId,
                    from: [lastMessage.from],
                    to: lastMessage.to,
                    cc: lastMessage.cc,
                    subject: lastMessage.subject,
                    html: lastMessage.html,
                    text: lastMessage.text,
                    date: lastMessage.date,
                  };
                  onReply({ email: emailForReply, all: false });
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDark 
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                <Reply className="w-4 h-4" />
                Reply
              </button>
              <button
                onClick={() => {
                  const lastMessage = threadMessages[threadMessages.length - 1];
                  const emailForReply = {
                    id: lastMessage.messageId,
                    messageId: lastMessage.messageId,
                    from: [lastMessage.from],
                    to: lastMessage.to,
                    cc: lastMessage.cc,
                    subject: lastMessage.subject,
                    html: lastMessage.html,
                    text: lastMessage.text,
                    date: lastMessage.date,
                  };
                  onReply({ email: emailForReply, all: true });
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <ReplyAll className="w-4 h-4" />
                Reply All
              </button>
            </>
          ) : (
            <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Reply className="w-4 h-4" />
              <span>Use your email client to reply to this thread</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
